/**
 * ============================================================================
 * Training 模块 API 工厂函数
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义 Training（培训管理）模块的所有 API 请求方法
 * - 包含：培训类型、培训任务、参与者管理、我的培训、培训统计
 * - 通过工厂模式实现请求客户端的依赖注入
 * - 提供完整的 TypeScript 类型安全
 * 
 * 🏭 工厂模式说明：
 * 本文件导出的是一个"工厂函数"，而非直接可用的 API 方法。
 * 你需要：
 * 1. 在项目中创建一个适配器，实现 IRequestClient 接口
 * 2. 调用工厂函数，传入适配器，获取 API 方法集合
 * 3. 在业务代码中使用获取到的 API 方法
 * 
 * 💡 使用示例：
 * 
 * **Web 端**：
 * ```typescript
 * import { createTrainingApi } from '@bayes-mes/shared/api/training/factory'
 * import { webAdapter } from './adapters/web'
 * 
 * const trainingApi = createTrainingApi(webAdapter)
 * 
 * // 获取培训任务列表
 * const tasks = await trainingApi.getTaskList({ status: 1 })
 * ```
 * 
 * **App 端**：
 * ```typescript
 * import { createTrainingApi } from '@bayes-mes/shared/api/training/factory'
 * import { uniAdapter } from './adapters/uni'
 * 
 * const trainingApi = createTrainingApi(uniAdapter)
 * 
 * // 获取我的培训任务
 * const myTasks = await trainingApi.getMyTaskList({ status: 'pending' })
 * ```
 * 
 * @module api/training/factory
 * @description Training（培训管理）模块的 API 工厂函数。
 * 本文件初版于 2025-12-10 生成，此后为手工维护文件（无生成器入口），
 * 变更须与后端 Resource/Request 同步并跑 type-check 与 validate:api-paths。
 * ============================================================================
 */

// ============================================================================
// 类型导入
// ============================================================================

import type {
  TrainingType,
  TrainingTask,
  TrainingParticipant,
  EmployeeTrainingRecord,
  TrainingPublishWarning,
  CreateTrainingTypeForm,
  UpdateTrainingTypeForm,
  TrainingTypeQueryParams,
  CreateTrainingTaskForm,
  UpdateTrainingTaskForm,
  TrainingTaskQueryParams,
  CreateMakeupTaskForm,
  CompleteTrainingTaskForm,
  MarkAttendanceForm,
  BatchMarkAttendanceForm,
  MyTrainingTask,
  MyTrainingQueryParams,
  TrainingRecordQueryParams,
  TrainingStatisticsOverview,
  TrainingStatisticsByDepartment,
  TrainingStatisticsByType,
  TrainingStatisticsByEmployee,
} from '../../types/training'

import type { PaginationResult } from '../../types/api'
import type { IRequestClient } from '../core/client'
import { API_ROUTES } from '../routes'

// ============================================================================
// Training API 工厂函数
// ============================================================================

/**
 * 创建 Training 模块的 API 方法集合
 * 
 * @param client - 请求客户端实例，需实现 IRequestClient 接口
 * @returns Training 模块的 API 方法集合
 */
export function createTrainingApi(client: IRequestClient) {
  return {
    // ============================================================================
    // 培训类型 API
    // ============================================================================
    
    /**
     * 获取培训类型列表（分页）
     * 
     * @param params - 查询参数
     * @returns 分页的培训类型列表
     */
    getTypeList(params?: TrainingTypeQueryParams) {
      return client.get<PaginationResult<TrainingType>>(
        API_ROUTES.training.list,
        { params }
      )
    },
    
    /**
     * 获取培训类型详情
     * 
     * @param id - 培训类型ID
     * @returns 培训类型详情
     */
    getTypeDetail(id: number) {
      return client.get<TrainingType>(API_ROUTES.training.detail(id))
    },
    
    /**
     * 创建培训类型
     * 
     * @param data - 创建表单数据
     * @returns 创建的培训类型
     */
    createType(data: CreateTrainingTypeForm) {
      return client.post<TrainingType>(API_ROUTES.training.types, data)
    },
    
    /**
     * 更新培训类型
     * 
     * @param id - 培训类型ID
     * @param data - 更新表单数据
     * @returns 更新后的培训类型
     */
    updateType(id: number, data: UpdateTrainingTypeForm) {
      return client.put<TrainingType>(API_ROUTES.training.trainingTypes(id), data)
    },
    
    /**
     * 删除培训类型
     * 
     * @param id - 培训类型ID
     */
    deleteType(id: number) {
      return client.delete<void>(API_ROUTES.training.delete(id))
    },
    
    /**
     * 切换培训类型状态（启用/禁用）
     * 
     * @param id - 培训类型ID
     * @returns 更新后的培训类型
     */
    toggleTypeStatus(id: number) {
      return client.post<TrainingType>(API_ROUTES.training.toggleStatus(id))
    },
    
    // ============================================================================
    // 培训任务 API
    // ============================================================================
    
    /**
     * 获取培训任务列表（分页）
     * 
     * @param params - 查询参数
     * @returns 分页的培训任务列表
     */
    getTaskList(params?: TrainingTaskQueryParams) {
      return client.get<PaginationResult<TrainingTask>>(
        API_ROUTES.training.tasks,
        { params }
      )
    },
    
    /**
     * 获取培训任务详情
     * 
     * @param id - 培训任务ID
     * @returns 培训任务详情（包含参与者、照片等关联数据）
     */
    getTaskDetail(id: number) {
      return client.get<TrainingTask>(API_ROUTES.training.trainingTasks(id))
    },
    
    /**
     * 创建培训任务
     * 
     * @param data - 创建表单数据
     * @returns 创建的培训任务
     */
    createTask(data: CreateTrainingTaskForm) {
      return client.post<TrainingTask>(API_ROUTES.training.tasks, data)
    },
    
    /**
     * 更新培训任务
     * 
     * @description 仅草稿状态的任务可以更新
     * @param id - 培训任务ID
     * @param data - 更新表单数据
     * @returns 更新后的培训任务
     */
    updateTask(id: number, data: UpdateTrainingTaskForm) {
      return client.put<TrainingTask>(API_ROUTES.training.trainingTasks(id), data)
    },
    
    /**
     * 删除培训任务
     * 
     * @description 仅草稿状态的任务可以删除
     * @param id - 培训任务ID
     */
    deleteTask(id: number) {
      return client.delete<void>(API_ROUTES.training.trainingTasks(id))
    },
    
    /**
     * 发布培训任务
     *
     * @description 发布后会向所有参与者发送通知
     * @param id - 培训任务ID
     * @returns 更新后的任务与发布警告（warnings 为非阻断提示，
     *   如参与者未关联系统账号；元素形状见 TrainingPublishWarning）
     */
    publishTask(id: number) {
      return client.post<{ task: TrainingTask, warnings: TrainingPublishWarning[] }>(API_ROUTES.training.publish(id))
    },
    
    /**
     * 取消培训任务
     * 
     * @param id - 培训任务ID
     * @param reason - 取消原因
     * @returns 更新后的培训任务
     */
    cancelTask(id: number, reason?: string) {
      return client.post<TrainingTask>(API_ROUTES.training.cancel(id), { reason })
    },
    
    /**
     * 开始培训
     * 
     * @description 开始培训并生成6位数字签到码
     * @param id - 培训任务ID
     * @returns 包含签到码信息的响应
     */
    startTask(id: number) {
      return client.post<{
        task: TrainingTask
        signInCode: string
        /** 签到码过期时间；后端可空（无过期语义时为 null） */
        expiresAt: string | null
      }>(API_ROUTES.training.start(id))
    },
    
    /**
     * 完成培训（讲师确认，终态结案任务）
     *
     * @description 序列化按载荷能力选择（2026-10-08 审计修复 P1-07）：
     * - photos 直传文件存在时走 multipart FormData（Web 端浏览器能力）；
     * - 仅 photo_ids 时走 JSON 请求体（App 六端统一文件库预上传后引用 ID），
     *   小程序/APP 容器的 uni.request 不支持 FormData，此前强制 FormData 会让
     *   photo_ids 在 H5/小程序请求体中丢失。
     * 照片二选一，合计至少 1 张、最多 9 张，另可携带备注。
     * @param id - 培训任务ID
     * @param data - 完成信息（照片二选一、备注可选）
     * @returns 更新后的培训任务
     */
    completeTask(id: number, data: CompleteTrainingTaskForm) {
      const hasDirectPhotos = Array.isArray(data.photos) && data.photos.length > 0
      if (!hasDirectPhotos) {
        const payload: Record<string, unknown> = {}
        if (data.remark !== undefined && data.remark !== '') {
          payload.remark = data.remark
        }
        payload.photo_ids = data.photo_ids ?? []
        return client.post<TrainingTask>(API_ROUTES.training.complete(id), payload)
      }

      const formData = new FormData()
      if (data.remark !== undefined && data.remark !== '') {
        formData.append('remark', data.remark)
      }
      data.photos?.forEach((photo) => {
        formData.append('photos[]', photo)
      })
      data.photo_ids?.forEach((fileId) => {
        formData.append('photo_ids[]', String(fileId))
      })
      return client.post<TrainingTask>(API_ROUTES.training.complete(id), formData)
    },
    
    /**
     * 关闭培训任务
     * 
     * @description 关闭后，未签到的参与者将自动标记为缺席
     * @param id - 培训任务ID
     * @returns 更新后的培训任务
     */
    closeTask(id: number) {
      return client.post<TrainingTask>(API_ROUTES.training.close(id))
    },
    
    // ============================================================================
    // 签到码 API
    // ============================================================================
    
    /**
     * 获取签到码
     * 
     * @param id - 培训任务ID
     * @returns 签到码信息
     */
    getSignInCode(id: number) {
      return client.get<{
        signInCode: string
        /** 签到码过期时间；后端可空（无过期语义时为 null） */
        expiresAt: string | null
        isExpired: boolean
      }>(API_ROUTES.training.tasksSignInCode(id))
    },
    
    /**
     * 刷新签到码
     * 
     * @description 生成新的签到码，旧签到码立即失效
     * @param id - 培训任务ID
     * @returns 新的签到码信息
     */
    refreshSignInCode(id: number) {
      return client.post<{
        signInCode: string
        /** 签到码过期时间；后端可空（无过期语义时为 null） */
        expiresAt: string | null
      }>(API_ROUTES.training.refreshSignInCode(id))
    },
    
    // ============================================================================
    // 补训 API
    // ============================================================================
    
    /**
     * 获取需要补训的人员列表
     * 
     * @param id - 培训任务ID
     * @returns 需要补训的参与者列表
     */
    getMakeupRequired(id: number) {
      return client.get<TrainingParticipant[]>(API_ROUTES.training.tasksMakeupRequired(id))
    },
    
    /**
     * 创建补训任务
     * 
     * @param id - 原培训任务ID
     * @param data - 补训任务信息
     * @returns 创建的补训任务
     */
    createMakeupTask(id: number, data: CreateMakeupTaskForm) {
      return client.post<TrainingTask>(API_ROUTES.training.createMakeup(id), data)
    },
    
    /**
     * 获取补训任务列表
     * 
     * @param id - 原培训任务ID
     * @returns 补训任务列表
     */
    getMakeupTasks(id: number) {
      return client.get<TrainingTask[]>(API_ROUTES.training.tasksMakeupTasks(id))
    },
    
    // ============================================================================
    // 参与者管理 API
    // ============================================================================
    
    /**
     * 获取参与者列表
     *
     * @param taskId - 培训任务ID
     * @param params - 查询参数（与后端筛选键一致，snake_case）
     * @returns 参与者列表
     */
    getParticipantList(taskId: number, params?: {
      status?: number
      attendance_status?: number
      needs_makeup?: number
      keyword?: string
    }) {
      return client.get<TrainingParticipant[]>(
        API_ROUTES.training.participants(taskId),
        { params }
      )
    },

    /**
     * 添加参与者
     *
     * @param taskId - 培训任务ID
     * @param employeeIds - 员工ID数组
     * @returns 无数据载荷（成功以 message 提示）
     */
    addParticipants(taskId: number, employeeIds: number[]) {
      return client.post<null>(
        API_ROUTES.training.participants(taskId),
        { employee_ids: employeeIds }
      )
    },

    /**
     * 按部门添加参与者
     *
     * @param taskId - 培训任务ID
     * @param departmentIds - 部门ID数组
     * @returns 实际添加的参与者人数（后端 success({count}) 载荷）
     */
    addParticipantsByDepartment(taskId: number, departmentIds: number[]) {
      return client.post<{ count: number }>(
        API_ROUTES.training.addByDepartment(taskId),
        { department_ids: departmentIds }
      )
    },
    
    /**
     * 移除参与者
     * 
     * @param taskId - 培训任务ID
     * @param participantId - 参与者ID
     */
    removeParticipant(taskId: number, participantId: number) {
      return client.delete<void>(API_ROUTES.training.tasksParticipants(taskId, participantId))
    },
    
    /**
     * 标记到场状态
     * 
     * @param participantId - 参与者ID
     * @param data - 到场信息
     * @returns 更新后的参与者
     */
    markAttendance(participantId: number, data: MarkAttendanceForm) {
      return client.post<TrainingParticipant>(
        API_ROUTES.training.markAttendance(participantId),
        data
      )
    },
    
    /**
     * 批量标记到场状态
     * 
     * @param taskId - 培训任务ID
     * @param data - 批量标记数据
     * @returns 更新的数量
     */
    batchMarkAttendance(taskId: number, data: BatchMarkAttendanceForm) {
      return client.post<{ count: number }>(
        API_ROUTES.training.batchMarkAttendance(taskId),
        data
      )
    },
    
    /**
     * 标记需要补训
     * 
     * @param participantId - 参与者ID
     * @param needsMakeup - 是否需要补训
     * @param remark - 补训原因
     * @returns 更新后的参与者
     */
    markMakeup(participantId: number, needsMakeup: boolean = true, remark?: string) {
      return client.post<TrainingParticipant>(
        API_ROUTES.training.markMakeup(participantId),
        { needs_makeup: needsMakeup, remark }
      )
    },
    
    /**
     * 批量标记需要补训
     * 
     * @param taskId - 培训任务ID
     * @param participantIds - 参与者ID数组
     * @param needsMakeup - 是否需要补训
     * @returns 更新的数量
     */
    batchMarkMakeup(taskId: number, participantIds: number[], needsMakeup: boolean = true) {
      return client.post<{ count: number }>(
        API_ROUTES.training.batchMarkMakeup(taskId),
        { participant_ids: participantIds, needs_makeup: needsMakeup }
      )
    },
    
    // ============================================================================
    // 我的培训 API（被培训人视角）
    // ============================================================================
    
    /**
     * 获取我的培训任务列表
     * 
     * @param params - 查询参数
     * @returns 我的培训任务列表
     */
    getMyTaskList(params?: MyTrainingQueryParams) {
      return client.get<PaginationResult<MyTrainingTask>>(
        API_ROUTES.training.myTasks,
        { params }
      )
    },
    
    /**
     * 获取我的培训任务详情
     * 
     * @param taskId - 培训任务ID
     * @returns 我的培训任务详情
     */
    getMyTaskDetail(taskId: number) {
      return client.get<MyTrainingTask>(API_ROUTES.training.trainingMyTasks(taskId))
    },
    
    /**
     * 确认收到培训通知
     * 
     * @param taskId - 培训任务ID
     * @returns 更新后的参与信息
     */
    confirmMyTask(taskId: number) {
      return client.post<MyTrainingTask>(API_ROUTES.training.confirm(taskId))
    },
    
    /**
     * 签到
     * 
     * @param taskId - 培训任务ID
     * @param signInCode - 6位数字签到码
     * @returns 更新后的参与信息
     */
    signIn(taskId: number, signInCode: string) {
      return client.post<MyTrainingTask>(
        API_ROUTES.training.signIn(taskId),
        { sign_in_code: signInCode }
      )
    },
    
    /**
     * 确认培训完成
     * 
     * @description 必须先签到才能确认完成
     * @param taskId - 培训任务ID
     * @returns 更新后的参与信息
     */
    completeMyTask(taskId: number) {
      return client.post<MyTrainingTask>(API_ROUTES.training.myTasksComplete(taskId))
    },
    
    /**
     * 获取我的培训记录
     * 
     * @param params - 查询参数
     * @returns 我的培训记录列表
     */
    getMyRecords(params?: TrainingRecordQueryParams) {
      return client.get<PaginationResult<EmployeeTrainingRecord>>(
        API_ROUTES.training.trainingMyRecords,
        { params }
      )
    },
    
    // ============================================================================
    // 培训统计 API
    // ============================================================================
    
    /**
     * 获取统计概览
     *
     * @param params - 筛选参数（snake_case），与任务列表筛选同口径（P2-12）
     * @returns 统计概览数据
     */
    getStatisticsOverview(params?: {
      department_id?: number
      date_from?: string
      date_to?: string
      keyword?: string
      training_type_id?: number
      training_level?: number
      is_makeup?: number
    }) {
      return client.get<TrainingStatisticsOverview>(
        API_ROUTES.training.statisticsOverview,
        { params }
      )
    },

    /**
     * 按部门统计
     *
     * @param params - 筛选参数
     * @returns 部门统计数据
     */
    getStatisticsByDepartment(params?: {
      date_from?: string
      date_to?: string
    }) {
      return client.get<TrainingStatisticsByDepartment[]>(
        API_ROUTES.training.statisticsByDepartment,
        { params }
      )
    },

    /**
     * 按培训类型统计
     *
     * @param params - 筛选参数
     * @returns 类型统计数据
     */
    getStatisticsByType(params?: {
      department_id?: number
      date_from?: string
      date_to?: string
    }) {
      return client.get<TrainingStatisticsByType[]>(
        API_ROUTES.training.statisticsByType,
        { params }
      )
    },

    /**
     * 按员工统计
     *
     * @param params - 筛选参数
     * @returns 员工统计数据（分页）
     */
    getStatisticsByEmployee(params?: {
      page?: number
      per_page?: number
      department_id?: number
      date_from?: string
      date_to?: string
    }) {
      // 后端直接 success() 包裸 LengthAwarePaginator：{data, total, current_page, per_page}
      return client.get<{
        data: TrainingStatisticsByEmployee[]
        total: number
        current_page: number
        per_page: number
      }>(
        API_ROUTES.training.statisticsByEmployee,
        { params }
      )
    },
  }
}

// ============================================================================
// 类型导出
// ============================================================================

/** Training API 类型 */
export type TrainingApi = ReturnType<typeof createTrainingApi>
